import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowUpRight, Loader2, Newspaper } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["All", "Training", "Race Reports", "Travel", "Nutrition", "Interviews"];

export default function Articles() {
  const [category, setCategory] = useState("All");
  const [articles, setArticles] = useState(null);

  useEffect(() => {
    setArticles(null);
    const params = category === "All" ? {} : { category };
    axios.get(`${API}/articles`, { params }).then((r) => setArticles(r.data)).catch(() => setArticles([]));
  }, [category]);

  return (
    <div className="min-h-screen bg-[#FBF9F5]">
      <PageHeader />
      <main className="px-6 sm:px-12 lg:px-20 pt-28 sm:pt-36 pb-24" data-testid="articles-page">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="mb-12">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4E00] mb-5 flex items-center gap-3">
            <Newspaper className="w-4 h-4" /> Articles
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-[#0D1117] tracking-tight mb-6">
            The Fitygrity <span className="italic text-[#FF4E00]">Journal.</span>
          </h1>
          <p className="text-base md:text-lg text-[#374151] max-w-2xl leading-relaxed">
            Training insight, race reports, travel notes, nutrition and conversations with the people who make start lines special.
          </p>
        </motion.div>

        <div className="flex flex-wrap gap-2 mb-12" data-testid="article-category-filters">
          {CATEGORIES.map((item) => (
            <button
              key={item}
              onClick={() => setCategory(item)}
              data-testid={`article-filter-${item.toLowerCase().replace(/\s+/g, "-")}`}
              className={`font-mono text-[10px] uppercase tracking-[0.18em] px-5 py-2.5 rounded-full border transition-all duration-300 ${
                category === item
                  ? "bg-[#FF4E00] border-[#FF4E00] text-[#FBF9F5]"
                  : "border-[#0D1117]/15 text-[#374151] hover:border-[#FF4E00]/50 hover:text-[#FF4E00]"
              }`}
            >
              {item}
            </button>
          ))}
        </div>

        {articles === null ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#FF4E00]" /></div>
        ) : articles.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-[#0D1117]/15 bg-[#FFFFFF] p-12 text-center" data-testid="articles-empty">
            <p className="font-serif text-2xl text-[#0D1117]">No articles here yet.</p>
            <p className="text-sm text-[#6B7280] mt-2">New stories will appear once Race Control publishes them.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="articles-grid">
            {articles.map((article, index) => (
              <motion.article
                key={article.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: (index % 3) * 0.08 }}
                className="group rounded-3xl border border-[#0D1117]/10 bg-[#FFFFFF] overflow-hidden hover:border-[#FF4E00]/40 transition-colors duration-300"
                data-testid="article-card"
              >
                <Link to={`/articles/${article.slug}`} className="block h-full">
                  <div className="aspect-[16/10] bg-[#F4F2EC] overflow-hidden">
                    {article.cover_url ? (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${article.cover_url}`} alt={article.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Newspaper className="w-10 h-10 text-[#FF4E00]/35" /></div>
                    )}
                  </div>
                  <div className="p-6 sm:p-7">
                    <div className="flex items-center justify-between gap-3 mb-4">
                      <span className="font-mono text-[9px] uppercase tracking-[0.18em] bg-[#FF4E00]/10 text-[#FF4E00] px-3 py-1.5 rounded-full">{article.category}</span>
                      <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-[#6B7280]">{article.read_time} min read</span>
                    </div>
                    <h2 className="font-serif text-xl sm:text-2xl text-[#0D1117] group-hover:text-[#FF4E00] transition-colors">{article.title}</h2>
                    <p className="text-sm text-[#374151] leading-relaxed mt-3 line-clamp-3">{article.excerpt}</p>
                    <div className="mt-5 flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.16em] text-[#6B7280]">
                      <span>{article.author_name} · {new Date(`${article.publish_date}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                      <ArrowUpRight className="w-3.5 h-3.5 text-[#FF4E00]" />
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
